<template>
  <div id="share-view" v-if="!isError">
    <header>
      <div class="logo">
        <h1>
          <a href="https://shadow.do" @click.prevent="handleLogoClick">
            Shadow
          </a>
        </h1>
      </div>
      <a
        id="download-shadow"
        href="https://shadow.do/download"
        @click.prevent="handleDownloadClick"
      >
        Download Shadow
      </a>
    </header>
    <main class="layout">
      <aside class="side left">
        <ul v-if="shareInfo && Array.isArray(shareInfo.sharedContent)">
          <li
            v-for="item in shareInfo.sharedContent"
            :key="item.skillCustIdx"
            :class="{ active: item.skillCustIdx === selectedSkillCustIdx }"
          >
            <a
              href="#"
              @click.prevent="selectSkill(item.skillCustIdx)"
              :title="item.skillDisplayName"
            >
              <template
                v-if="skillIcons[item.skillCustIdx]?.type === 'material'"
              >
                <span class="skill-icon material-icons">
                  {{ skillIcons[item.skillCustIdx].name }}
                </span>
              </template>
              <template v-else>
                <span
                  class="skill-icon"
                  :style="`-webkit-mask: url('${
                    skillIcons[item.skillCustIdx]?.url
                  }') no-repeat center / 20px 20px; mask: url('${
                    skillIcons[item.skillCustIdx]?.url
                  }') no-repeat center / 20px 20px;`"
                  >skillIcon</span
                >
              </template>
              <span class="skill-label">{{ item.skillDisplayName }}</span>
            </a>
          </li>
        </ul>
      </aside>
      <section class="center">
        <div v-if="shareInfo" :class="['content-wrap', { loading: isLoading }]">
          <div id="info">
            <p id="title-wrapper">
              <span id="title">{{ shareInfo.convTitle }}</span>
              <button class="copy-btn" @click="copy">
                <img src="@/assets/img/copy.svg" alt="Copy" />
              </button>
            </p>
            <div id="meta">
              <span>{{ formattedConvStartedAt }}</span>
              <span>{{ formattedConvFileDuration }}</span>
              <div class="name-wrapper" @click.stop="toggleAttendeesPopover">
                <div class="meta-wrapper">
                  <circle-avatar
                    :attendee="shareInfo.attendees[0]"
                    :radius="9"
                  />
                  <span>{{ formattedDisplayName }}</span>
                </div>

                <div
                  v-if="isAttendeesPopoverOpen"
                  class="attendees-popover"
                  ref="popover"
                  @click.stop
                >
                  <!-- <button class="close-btn" @click.stop="closeAttendeesPopover">
                    ✕
                  </button> -->
                  <p>
                    <span
                      >Attendee{{
                        shareInfo.attendees.length == 1 ? "" : "s"
                      }}</span
                    >
                    <span>{{ shareInfo.attendees.length }}</span>
                  </p>
                  <ul>
                    <li
                      v-for="(attendee, idx) in shareInfo.attendees"
                      :key="idx"
                    >
                      <circle-avatar :attendee="attendee" :radius="12.5" />
                      <p>
                        <span class="name">{{ attendee.displayName }}</span>
                        <span class="email">{{ attendee.email }}</span>
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div
            id="content"
            v-if="selectedContent"
            v-html="renderedContent"
          ></div>
        </div>
        <div class="ispinner white animating" v-if="isLoading">
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
          <div class="ispinner-blade"></div>
        </div>
      </section>
      <aside class="side right"></aside>
    </main>
  </div>
  <div id="share-view-error" v-else>
    <header>
      <header>
        <div class="logo">
          <h1>Shadow</h1>
        </div>
        <h2>This page couldn’t be found</h2>
        <p>
          You may not have access, or it might have been deleted or moved.<br />
          Check the link and try again.
        </p>
      </header>
      <main>
        <div class="btn-group-wrap">
          <button id="btn-go-back" @click="$router.back">Go back</button>
        </div>
      </main>
    </header>
  </div>
</template>

<script>
import { marked } from "marked";
import CircleAvatar from "./components/CircleAvatar.vue";

export default {
  components: { CircleAvatar },
  name: "ShareView",
  props: {
    shareId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      apiBaseUrl: "https://d3ea3io1axhl6i.cloudfront.net/dev",
      // skillImagePath: "https://web.shadow.do/shadow-resource/img/skills",
      skillImagePath: "/shadow-resource/img/skills",
      isLoading: true,
      shareInfo: null,
      selectedSkillCustIdx: null,
      selectedContent: null,
      isError: false,
      isAttendeesPopoverOpen: false,
    };
  },
  computed: {
    skillIcons() {
      const list = this.shareInfo?.sharedContent ?? [];
      const iconsMap = {};

      list.forEach((item) => {
        if (item.skillCustIcon) {
          iconsMap[item.skillCustIdx] = {
            type: "material",
            name: item.skillCustIcon,
          };
        } else {
          const iconName = item.skillIcon || item.skillId;
          iconsMap[item.skillCustIdx] = {
            type: "svg",
            url: `${this.skillImagePath}/${iconName}.svg`,
          };
        }
      });

      return iconsMap;
    },
    formattedConvStartedAt() {
      const d = new Date(this.shareInfo?.convStartedAt);
      return this.shareInfo?.convStartedAt
        ? `${d.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })} · ${d.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}`
        : "";
    },
    formattedConvFileDuration() {
      if (!this.shareInfo?.convFileDuration) return "0:00";
      const seconds = Number(this.shareInfo.convFileDuration);
      if (Number.isNaN(seconds)) return "0:00";

      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);

      const twoDigits = (n) => n.toString().padStart(2, "0");
      const minuteStr = h > 0 ? twoDigits(m) : m.toString();
      const secondStr = twoDigits(s);

      return (h > 0 ? `${h}:` : "") + `${minuteStr}:${secondStr}`;
    },
    formattedDisplayName() {
      const attendees = this.shareInfo?.attendees ?? [];
      const attendeesCount = attendees.length;

      if (attendeesCount === 0) return "";

      // 메인으로 표시할 참석자 선택
      let mainAttendee;
      if (attendees[0].isMe && attendeesCount > 1) {
        mainAttendee = attendees[1];
      } else {
        mainAttendee = attendees[0];
      }

      // +n 표시 (전체 - 메인 1명)
      const othersCount = attendeesCount - 1;

      return `${mainAttendee.displayName}${
        othersCount > 0 ? ` +${othersCount}` : ""
      }`;
    },
    renderedContent() {
      const content = this.selectedContent?.skillResultContent;
      if (!content) return "";

      if (this.selectedContent.skillId === "exportTranscript") {
        const lines = content.split("\n").filter((line) => line.trim() !== "");

        const htmlLines = lines.map((line) => {
          return line.replace(/^\*(.+?):\*/g, "<strong>$1:</strong>");
        });

        return htmlLines.map((line) => `<p>${line}</p>`).join("");
      }

      return marked(content);
    },
    selectedSkillItem() {
      if (!this.shareInfo?.sharedContent || this.selectedSkillCustIdx === null)
        return null;

      return this.shareInfo.sharedContent.find(
        (item) => item.skillCustIdx === this.selectedSkillCustIdx
      );
    },
    selectedSkillId() {
      return this.selectedSkillItem?.skillId ?? null;
    },
    selectedSkillCustName() {
      return this.selectedSkillItem?.skillCustName ?? null;
    },
    selectedSkillDisplayName() {
      return this.selectedSkillItem?.skillDisplayName ?? null;
    },
  },
  watch: {
    shareInfo(newVal) {
      if (!newVal?.sharedContent?.length) return;

      const routeSkillIdx = this.$route?.params?.skillCustIdx ?? null;

      const validIdx = this.resolveValidSkillIdx(routeSkillIdx);

      if (validIdx !== null) {
        this.selectSkill(validIdx, false);
      } else {
        this.handleInvalidSkillCustIdx();
      }
    },
    "$route.params.skillCustIdx"(newIdx) {
      if (!this.shareInfo?.sharedContent?.length) return;

      if (this.isError) this.isError = false;

      const validIdx = this.resolveValidSkillIdx(newIdx);

      if (validIdx !== null) {
        this.selectSkill(validIdx, false);
      } else {
        this.handleInvalidSkillCustIdx();
      }
    },
  },
  async mounted() {
    await this.fetchShareInfo();
  },
  methods: {
    async fetchShareInfo() {
      try {
        this.isLoading = true;
        this.isError = false;

        const url = `${this.apiBaseUrl}/share/info?shareId=${this.shareId}`;
        const response = await fetch(url, { method: "GET" });

        if (response.status === 204) throw new Error("데이터 없음");
        if (!response.ok) throw new Error("API 응답 오류");

        const data = await response.json();

        this.shareInfo = data;
      } catch (error) {
        console.error("공유 정보 조회 실패: ", error);
        this.isError = true;
      } finally {
        this.isLoading = false;
      }
    },

    resolveValidSkillIdx(skillCustIdx) {
      const list = this.shareInfo.sharedContent;
      if (!list || list.length === 0) {
        console.error("sharedContent가 비어있음");
        return null;
      }

      if (skillCustIdx === null || skillCustIdx.length === 0) {
        return list[0].skillCustIdx;
      }

      const parsed = Number(skillCustIdx);

      if (Number.isNaN(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
        console.error(`skillCustIdx 유효하지 않음: ${skillCustIdx}`);
        return null;
      }

      const exists = list.some((item) => Number(item.skillCustIdx) === parsed);

      if (!exists) {
        console.error(`존재하지 않는 skillCustIdx: ${parsed}`);
        return null;
      }

      return parsed;
    },
    handleInvalidSkillCustIdx() {
      console.error("skillCustIdx 유효하지 않음, 선택 초기화");

      this.selectedSkillCustIdx = null;
      this.selectedContent = null;
      this.isError = true;
    },
    async selectSkill(skillCustIdx, pushHistory = true) {
      if (this.selectedSkillCustIdx === skillCustIdx) return;

      this.selectedSkillCustIdx = skillCustIdx;

      if (pushHistory) {
        this.$router.push({
          name: "Share",
          params: {
            shareId: this.shareId,
            skillCustIdx,
          },
        });

        await this.analytics({ name: "switch_tab" });
      }

      await this.fetchShareContent(skillCustIdx);
    },

    async fetchShareContent(skillCustIdx) {
      try {
        this.isLoading = true;
        this.selectedContent = null;
        this.isError = false;

        const url = `${this.apiBaseUrl}/share/content?shareId=${this.shareId}&skillCustIdx=${skillCustIdx}`;
        const response = await fetch(url);

        if (response.status === 204) throw new Error("데이터 없음");
        if (!response.ok) throw new Error("API 응답 오류");

        const data = await response.json();

        this.selectedContent = data;

        await this.analytics({ name: "view_content" });
      } catch (error) {
        console.error("공유 데이터 조회 실패: ", error);
        this.isError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async copy() {
      if (!this.selectedContent || !this.selectedContent.skillResultContent) {
        console.warn("복사할 내용 없음");
        return;
      }

      let input = this.selectedContent.skillResultContent;

      // 1. timestamp 링크 제거
      input = input.replace(
        /(\[\d{1,2}:\d{2}(?::\d{2})?\]\(timestamp:.+?\))|(\(timestamp:.+?\))/g,
        ""
      );

      // 2. "Timestamps: ..." 형태의 줄 제거
      input = input.replace(/^Timestamps:.*$/gm, "");

      // 3. [텍스트](...) 형태에서 대괄호 안의 텍스트만 추출
      input = input.replace(/\[([^\]]+)\]\(.*?\)/g, (_, p1) => p1);

      // 클립보드 복사
      navigator.clipboard
        .writeText(input)
        .then(() => {
          // 원하면 토스트나 알림 표시 가능
          console.log("복사 성공");
        })
        .catch((err) => {
          console.error("복사 실패:", err);
        });

      await this.analytics({ name: "copy_content" });
    },
    toggleAttendeesPopover() {
      this.isAttendeesPopoverOpen = !this.isAttendeesPopoverOpen;

      if (this.isAttendeesPopoverOpen) {
        // nextTick으로 렌더링 후 이벤트 리스너 등록
        this.$nextTick(() => {
          document.addEventListener("click", this.handleOutsideClick);
        });
      } else {
        document.removeEventListener("click", this.handleOutsideClick);
      }
    },
    closeAttendeesPopover() {
      this.isAttendeesPopoverOpen = false;
      document.removeEventListener("click", this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      const popover = this.$refs.popover;
      if (popover && !popover.contains(event.target)) {
        this.closeAttendeesPopover();
      }
    },

    async analytics({ name }) {
      try {
        if (!name) throw new Error("Analytics 이벤트 이름 필요");

        // user agent
        const userAgent = navigator.userAgent;

        // page path
        const pagePath = this.$route?.fullPath || window.location.pathname;

        // params 구성
        const params = { name, pagePath, userAgent };
        params.parameters = {
          shareId: this.shareInfo?.shareId ?? null,
          convUuid: this.shareInfo?.convUuid ?? null,
          ownerUid: this.shareInfo?.userUid ?? null,
          skillCustIdx: this.selectedSkillCustIdx,
          skillId: this.selectedSkillId,
          skillCustName: this.selectedSkillCustName,
          skillDisplayName: this.selectedSkillDisplayName,
        };

        // API 호출
        const response = await fetch(`${this.apiBaseUrl}/analytics`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          console.error("Analytics 전송 실패", response.status);
        } else {
          console.log("Analytics 전송 성공");
        }

        return response;
      } catch (err) {
        console.error("Analytics 에러:", err);
        throw err;
      }
    },
    async handleLogoClick() {
      try {
        await this.analytics({ name: "click_logo" });
      } finally {
        // Analytics 호출 후 이동
        window.location.href = "https://shadow.do";
      }
    },

    async handleDownloadClick() {
      try {
        await this.analytics({ name: "click_download" });
      } finally {
        // Analytics 호출 후 이동
        window.location.href = "https://shadow.do/download";
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
};
</script>

<style>
body:has(#share-view),
body:has(#share-view-error) {
  height: 100%;
  margin: 0;
  overflow: hidden;
  background: #151515;
  color: #fff;
}
</style>

<style scoped>
#share-view-error {
  max-width: 720px;
  margin: 0 auto;
  padding: 5.587% 6.9835%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

#share-view-error header {
  margin-bottom: 24px;
  text-align: center;
}

#share-view-error header .logo {
  margin-bottom: 60px;
}

#share-view-error header .logo h1 {
  width: 120px;
  height: 31.2px;
  text-indent: -9999px;
  margin: 0 auto;
  background-image: url("@/assets/img/logo.png");
  background-size: cover;
  border-radius: 4px;
}

#share-view-error header h2 {
  font-size: 18px;
  line-height: 32px;
  font-weight: 400;
  color: #fff;
}

#share-view-error header p {
  margin: 24px 0;
  font-size: 14px;
  line-height: 25px;
  font-weight: 200;
  color: #aaa;
}

#share-view-error button {
  position: relative;
  font-size: 14px;
  font-weight: 400;
  line-height: 25px;
  padding: 0 6px;
  border-radius: 7px;
  color: #aaa;
  cursor: pointer;
  text-align: center;
}

#share-view-error div.btn-group-wrap {
  margin-top: 12px;
  text-align: center;
}

#share-view-error div.btn-group-wrap:first-child {
  margin-top: 0;
}

#share-view-error #btn-go-back {
  width: 300px;
  height: 37px;
  border: 0.5px solid #bb4a00;
  background: #bb4a00;
  color: #fff;
}

#share-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

#share-view header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 72px;
  border-bottom: 1px solid #333;
}

#share-view header .logo {
  margin: 0;
}

#share-view header .logo h1 {
  margin: 0;
}

#share-view header .logo a {
  display: block;
  width: 120px;
  height: 31.2px;
  text-indent: -9999px;
  background-image: url("@/assets/img/logo.png");
  background-size: cover;
  background-position: center;
}

#download-shadow {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 170px;
  height: 40px;
  background-color: #bb4a00;
  border: 0.5px solid #999;
  border-radius: 7px;
  font-size: 16px;
  text-align: center;
}

.layout {
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 0 10px;
  overflow: hidden;
}

.center {
  width: 760px;
  padding: 30px 30px 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

#info {
  flex-shrink: 0;
}

#title-wrapper {
  position: relative;
  margin-bottom: 8px;
  font-size: 20px;
  line-height: 1.5;
  font-weight: 400;
  padding-right: 30px;
}

#title {
  display: block;
  word-break: break-word;
}

.copy-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.copy-btn img {
  width: 20px;
  height: 20px;
}

#info #meta {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 13px;
  font-size: 13px;
  line-height: 23px;
  font-weight: 300;
  color: #777;
  border-bottom: 0.5px solid #333;
}

#info #meta .name-wrapper {
  position: relative; /* 팝오버 기준 */
  cursor: pointer;
}

#info #meta .name-wrapper .meta-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

#info #meta .name-wrapper .meta-wrapper:hover > span {
  color: #fff;
}

.center > .content-wrap {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.center > .content-wrap:not(.loading) {
  height: 100%;
}

::v-deep(#content) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Firefox ONLY */
@-moz-document url-prefix() {
  ::v-deep(#content) {
    scrollbar-width: thin;
    scrollbar-color: rgba(57, 57, 57, 0.7) transparent;
  }
}

::v-deep(#content::-webkit-scrollbar) {
  width: 6px;
}

::v-deep(#content::-webkit-scrollbar-track) {
  background: transparent;
}

::v-deep(#content::-webkit-scrollbar-thumb) {
  background-color: rgba(57, 57, 57, 0.7);
  border-radius: 6px;
  cursor: grab;
}

::v-deep(#content::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(57, 57, 57, 0.85);
}

::v-deep(#content::-webkit-scrollbar-thumb:active) {
  cursor: grabbing;
}

/* #content 전체 스타일 */
::v-deep(#content) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  color: #ddd;
  line-height: 150%;
  padding: 18px 30px 50px 30px;
  caret-color: #d65d0e;
}

::v-deep(#content *) {
  all: unset;
  display: revert;
}

/* 첫번째 요소 margin 제거 */
::v-deep(#content :first-child) {
  margin-top: 0;
}

/* ul, ol 스타일 */
::v-deep(#content ul),
::v-deep(#content ol) {
  margin: 0 0 24px 0;
  padding: 0 1rem;
  line-height: 140%;
}

/* li 스타일 */
::v-deep(#content li) {
  font-size: 14px;
  font-weight: 400;
  margin: 0 0 8px 0;
}
::v-deep(#content li p) {
  margin: 4px 0;
}
::v-deep(#content li > ul),
::v-deep(#content li > ol) {
  margin-bottom: 0;
}

/* 제목 스타일 */
::v-deep(#content h1) {
  font-size: 20px;
  font-weight: 500;
  margin: 36px 0 12px 0;
  line-height: 150%;
}
::v-deep(#content h2) {
  font-size: 18px;
  font-weight: 500;
  margin: 32px 0 12px 0;
  line-height: 150%;
}
::v-deep(#content h3) {
  font-size: 16px;
  font-weight: 500;
  margin: 28px 0 10px 0;
  line-height: 150%;
}
::v-deep(#content h4) {
  font-size: 15px;
  font-weight: 500;
  margin: 24px 0 10px 0;
  line-height: 150%;
}
::v-deep(#content h5) {
  font-size: 14px;
  font-weight: 400;
  margin: 20px 0 8px 0;
  line-height: 150%;
}
::v-deep(#content h6) {
  font-size: 14px;
  font-weight: 400;
  margin: 18px 0 8px 0;
  line-height: 140%;
}

/* bold, strong */
::v-deep(#content b),
::v-deep(#content strong) {
  font-weight: 500;
}

/* p */
::v-deep(#content p) {
  font-size: 14px;
  font-weight: 400;
  margin: 0 0 16px 0;
}

/* code & custom code block */
::v-deep(#content code) {
  font-family: monospace;
  background-color: #2d2d2d;
  color: #d65d0e;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 13px;
}

::v-deep(#content .custom-code-block) {
  background-color: #2d2d2d;
  color: #d65d0e;
  padding: 12px;
  border-radius: 8px;
  font-family: "Source Code Pro", monospace;
  font-size: 13px;
  overflow-x: auto;
  white-space: pre;
}

/* hr */
::v-deep(#content hr) {
  background: #3b3b3b;
  height: 1px;
  border: 0;
  margin: 24px 0;
}

/* a 링크 */
::v-deep(#content a) {
  color: #9f9f9f;
  border-bottom: 1px solid #9f9f9f;
  text-decoration: none;
  cursor: pointer;
}
::v-deep(#content a[href^="timestamp"]) {
  padding: 3px 6px;
  font-size: 10px;
  font-weight: 300;
  line-height: 12px;
  margin-left: 6px;
  border: 0;
  border-radius: 15px;
  background-color: #333;
  color: #aaa;
}
::v-deep(#content a[href^="timestamp"]:focus),
::v-deep(#content a[href^="timestamp"]:hover) {
  color: #fff;
}

/* blockquote */
::v-deep(#content blockquote) {
  display: flex;
  border: none;
  margin: 10px 0;
}
::v-deep(#content blockquote::before) {
  content: "";
  display: block;
  width: 5px;
  border-radius: 8px 0 0 8px;
  background-color: #e8eaed;
}
::v-deep(#content blockquote > p) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  margin: 0;
  padding: 12px;
  border-radius: 0 8px 8px 0;
  flex-grow: 1;
}

/* table */
::v-deep(#content table) {
  border-collapse: collapse;
  width: 100%;
}
::v-deep(#content th),
::v-deep(#content td) {
  font-size: 14px;
  font-weight: 400;
  border: 0.5px solid #777777;
  padding: 12px 8px;
  text-align: left;
  color: #ddd;
  vertical-align: middle;
}

/* 선택 영역 */
::v-deep(#content::selection),
::v-deep(#content *::selection),
::v-deep(#content * ::selection) {
  background-color: rgba(187, 74, 0, 0.5);
}

.side {
  display: flex;
  justify-content: left;
  align-items: flex-start;
  flex-shrink: 1;
  width: clamp(120px, calc((100vw - 760px - 20px) / 2), 236px);
  max-width: 236px;
  min-width: 0;
  height: 100%;
}

.side.left ul {
  min-width: 180px;
  margin-top: 120px;
  padding: 4px;
  border-radius: 24px;
  background-color: #1e1e1eb3;
  box-shadow: 0px 0px 24px 0px #77777755;
}
.side.left ul li {
  display: block;
  padding: 1px;
}

.side.left ul li:last-child {
  margin-bottom: 0;
}

.side.left ul li a {
  display: block;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 23px;
  color: #777777;
  width: 100%;
  padding: 8px 6px;
  border-radius: 24px;
  box-sizing: border-box;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 170px;
}

.side.left ul li:hover a {
  color: #bb4a00;
  background-color: #77777708;
}

.side.left ul li.active a {
  color: #bb4a00;
  background-color: #bb4a0008;
}

.side.left ul li .skill-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  -webkit-mask-size: 20px 20px;
  mask-size: 20px 20px;
  margin-right: 6px;
  background-color: #7a7a7a;
  text-indent: -9999px;
}

.side.left ul li:hover .skill-icon,
.side.left ul li.active .skill-icon {
  background-color: #bb4a00;
}

.side.left ul li .skill-icon.material-icons {
  font-family: "Material Icons" !important;
  font-size: 20px;
  width: 20px;
  height: 20px;
  -webkit-mask: none !important;
  mask: none !important;
  background-color: transparent !important;
  color: #7a7a7a;
  text-indent: 0 !important;
  vertical-align: middle;
  font-style: normal;
  letter-spacing: normal;
  display: inline-block;
}

.side.left ul li:hover .skill-icon.material-icons,
.side.left ul li.active .skill-icon.material-icons {
  color: #bb4a00;
}

@media (max-width: 1200px) {
  header {
    padding: 0 16px;
  }

  .layout {
    gap: 0;
    padding: 0;
  }

  .center {
    width: 100%;
    max-width: 1024px;
  }

  .side {
    display: none;
  }
}

@media (max-width: 800px) {
  #share-view header {
    height: 50px;
  }

  #share-view header .logo a {
    width: 100px;
    height: 26px;
  }

  #download-shadow {
    width: 150px;
    height: 30px;
    font-size: 13px;
  }
}

@media (max-width: 479px) {
  .center {
    padding: 0;
  }

  #info {
    padding: 0 10px;
  }

  #info #title-wrapper {
    padding-left: 10px;
  }

  #info #meta {
    padding: 0 10px 13px;
  }

  .copy-btn {
    right: 0;
  }
}

.ispinner {
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  margin: 100px auto;
}
.ispinner .ispinner-blade {
  position: absolute;
  left: 44.5%;
  top: 37%;
  width: 10%;
  height: 35%;
  border-radius: 50%/20%;
  -webkit-animation: ispinner-fade 1s linear infinite;
  animation: ispinner-fade 1s linear infinite;
  -webkit-animation-play-state: paused;
  animation-play-state: paused;
}
.ispinner .ispinner-blade:nth-child(1) {
  -webkit-animation-delay: -1.66667s;
  animation-delay: -1.66667s;
  -webkit-transform: rotate(30deg) translate(0, -150%);
  transform: rotate(0) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(2) {
  -webkit-animation-delay: -1.58333s;
  animation-delay: -1.58333s;
  -webkit-transform: rotate(60deg) translate(0, -150%);
  transform: rotate(45deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(3) {
  -webkit-animation-delay: -1.5s;
  animation-delay: -1.5s;
  -webkit-transform: rotate(90deg) translate(0, -150%);
  transform: rotate(90deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(4) {
  -webkit-animation-delay: -1.41667s;
  animation-delay: -1.41667s;
  -webkit-transform: rotate(120deg) translate(0, -150%);
  transform: rotate(135deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(5) {
  -webkit-animation-delay: -1.33333s;
  animation-delay: -1.33333s;
  -webkit-transform: rotate(150deg) translate(0, -150%);
  transform: rotate(180deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(6) {
  -webkit-animation-delay: -1.25s;
  animation-delay: -1.25s;
  -webkit-transform: rotate(180deg) translate(0, -150%);
  transform: rotate(225deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(7) {
  -webkit-animation-delay: -1.16667s;
  animation-delay: -1.16667s;
  -webkit-transform: rotate(210deg) translate(0, -150%);
  transform: rotate(270deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(8) {
  -webkit-animation-delay: -1.08333s;
  animation-delay: -1.08333s;
  -webkit-transform: rotate(240deg) translate(0, -150%);
  transform: rotate(315deg) translate(0, -100%);
}
.ispinner .ispinner-blade:nth-child(9) {
  -webkit-animation-delay: -1s;
  animation-delay: -1s;
  -webkit-transform: rotate(270deg) translate(0, -150%);
  transform: rotate(270deg) translate(0, -150%);
}
.ispinner .ispinner-blade:nth-child(10) {
  -webkit-animation-delay: -0.91667s;
  animation-delay: -0.91667s;
  -webkit-transform: rotate(300deg) translate(0, -150%);
  transform: rotate(300deg) translate(0, -150%);
}
.ispinner .ispinner-blade:nth-child(11) {
  -webkit-animation-delay: -0.83333s;
  animation-delay: -0.83333s;
  -webkit-transform: rotate(330deg) translate(0, -150%);
  transform: rotate(330deg) translate(0, -150%);
}
.ispinner .ispinner-blade:nth-child(12) {
  -webkit-animation-delay: -0.75s;
  animation-delay: -0.75s;
  -webkit-transform: rotate(360deg) translate(0, -150%);
  transform: rotate(360deg) translate(0, -150%);
}
.ispinner.animating .ispinner-blade {
  -webkit-animation-play-state: running;
  animation-play-state: running;
}
.ispinner.white .ispinner-blade {
  background-color: white;
}
.ispinner.gray .ispinner-blade {
  background-color: #8c8c8c;
}
.ispinner.large {
  width: 35px;
  height: 35px;
}
.ispinner.large .ispinner-blade {
  width: 8.57143%;
  height: 25.71429%;
  border-radius: 50%/16.67%;
}

@-webkit-keyframes ispinner-fade {
  0% {
    opacity: 0.85;
  }
  50% {
    opacity: 0.25;
  }
  100% {
    opacity: 0.25;
  }
}
@keyframes ispinner-fade {
  0% {
    opacity: 0.85;
  }
  50% {
    opacity: 0.25;
  }
  100% {
    opacity: 0.25;
  }
}

.attendees-popover {
  position: absolute;
  top: 100%;
  left: -16px;
  margin-top: 5px;
  background-color: #1e1e1e;
  border: 1px solid #333;
  border-radius: 8px;
  width: 205px;
  z-index: 9999;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  cursor: default;
}

.attendees-popover > p {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11.5px 15px 6.5px;
  font-size: 14px;
  border-bottom: 0.5px solid #333;
  color: #777;
}

.attendees-popover ul {
  padding: 10px 15px;
}

.attendees-popover li {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  font-size: 13px;
}

.attendees-popover li p {
  display: flex;
  flex-direction: column;
  max-width: calc(205px - 65px);
}

.attendees-popover li p > * {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attendees-popover li p .name {
  color: #eee;
}
.attendees-popover li p .email {
  color: #777;
}

/* .attendees-popover .close-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: none;
  border: none;
  color: #aaa;
  font-size: 12px;
  cursor: pointer;
}
.attendees-popover .close-btn:hover {
  color: #fff;
} */
</style>
